import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  Settings, Users, DollarSign, Clock, Save, Plus, Trash2, Loader2,
  GripVertical, FileText, CheckCircle2, ChevronDown, ChevronUp, Shield, Mail, UserPlus,
  MapPin, Percent, Building2, Tag, Layers, BarChart3, RefreshCw, ListChecks, ArrowUpDown,
  Send, ServerCog
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSettingsPanel({ orgId, teams = [] }) {
  const { isDark } = useTheme();
  const [orgCutoff, setOrgCutoff] = useState({ age_cutoff_month: null, age_cutoff_day: null });
  const [cutoffSaving, setCutoffSaving] = useState(false);
  const [cutoffSaved, setCutoffSaved] = useState(false);
  useEffect(() => {
    if (!orgId) return;
    axios.get(`${API_URL}/api/organizations/${orgId}/age-cutoff`)
      .then(({ data }) => setOrgCutoff({ age_cutoff_month: data.age_cutoff_month ?? null, age_cutoff_day: data.age_cutoff_day ?? null }))
      .catch(() => {});
  }, [orgId]);
  const saveOrgCutoff = async () => {
    setCutoffSaving(true); setCutoffSaved(false);
    try {
      await axios.put(`${API_URL}/api/organizations/${orgId}/age-cutoff`, {
        age_cutoff_month: parseInt(orgCutoff.age_cutoff_month, 10),
        age_cutoff_day: parseInt(orgCutoff.age_cutoff_day, 10),
      });
      setCutoffSaved(true); setTimeout(() => setCutoffSaved(false), 2500);
    } catch (e) { /* surfaced inline */ }
    finally { setCutoffSaving(false); }
  };
  const [activeSection, setActiveSection] = useState('teams');
  const [editTeam, setEditTeam] = useState(null);
  const [regConfig, setRegConfig] = useState(null);
  const [approvedTeams, setApprovedTeams] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [showAddApproved, setShowAddApproved] = useState(false);
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [teamCoaches, setTeamCoaches] = useState({});
  const [showInviteCoach, setShowInviteCoach] = useState(null); // team_id or null
  const [inviteForm, setInviteForm] = useState({ email: '', first_name: '', last_name: '', coach_type: 'head' });
  const [crossTeamVisibility, setCrossTeamVisibility] = useState(false);
  const [assistantRestricted, setAssistantRestricted] = useState(false);
  const [orgData, setOrgData] = useState(null);
  const [venues, setVenues] = useState([]);
  const [sports, setSports] = useState([]);
  const [showAddVenue, setShowAddVenue] = useState(false);
  const [venueForm, setVenueForm] = useState({ name: '', address: '' });
  const [discountForm, setDiscountForm] = useState({ multi_child_discount_pct: 0, coach_parent_discount_pct: 0, scholarship_discount_pct: 0, early_bird_discount_pct: 0, early_bird_deadline: '' });
  const [scheduleForm, setScheduleForm] = useState({ enabled: false, installments: 2, interval_days: 30 });
  const [volCategories, setVolCategories] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [schoolYearStart, setSchoolYearStart] = useState(8);
  const [newCategory, setNewCategory] = useState('');
  const [newDocType, setNewDocType] = useState('');
  const [divisions, setDivisions] = useState([]);
  const [showAddDivision, setShowAddDivision] = useState(false);
  const [teamCapacity, setTeamCapacity] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [statConfig, setStatConfig] = useState(null);
  const [leagueId, setLeagueId] = useState('');

  const fetchRegConfig = useCallback(async () => {
    if (!orgId) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/registration/config/${orgId}`);
      setRegConfig(data);
    } catch (err) { console.error('Reg config fetch error:', err); }
  }, [orgId]);

  const fetchApprovedTeams = useCallback(async () => {
    if (!orgId) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/registration/approved-teams/${orgId}`);
      setApprovedTeams(data);
    } catch (err) { console.error('Approved teams fetch error:', err); }
  }, [orgId]);

  useEffect(() => { fetchRegConfig(); fetchApprovedTeams(); }, [fetchRegConfig, fetchApprovedTeams]);

  // Fetch coaches for all teams
  useEffect(() => {
    const fetchCoaches = async () => {
      const coaches = {};
      for (const team of teams) {
        try {
          const { data } = await axios.get(`${API_URL}/api/coaches/team/${team.id}`);
          coaches[team.id] = data;
        } catch { coaches[team.id] = { coaches: [] }; }
      }
      setTeamCoaches(coaches);
    };
    if (teams.length > 0) fetchCoaches();
  }, [teams]);

  // Fetch org settings, venues, sports
  useEffect(() => {
    const fetchOrgSettings = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/organizations`);
        const org = data.find(o => o.id === orgId);
        if (org) {
          setOrgData(org);
          const s = org.settings || {};
          setCrossTeamVisibility(s.coaches_cross_team_visibility || false);
          setAssistantRestricted(s.assistant_coach_restricted || false);
          setDiscountForm({
            multi_child_discount_pct: s.multi_child_discount_pct || 0,
            coach_parent_discount_pct: s.coach_parent_discount_pct || 0,
            scholarship_discount_pct: s.scholarship_discount_pct || 0,
            early_bird_discount_pct: s.early_bird_discount_pct || 0,
            early_bird_deadline: s.early_bird_deadline || '',
          });
          setScheduleForm(s.payment_schedule || { enabled: false, installments: 2, interval_days: 30 });
          setVolCategories(s.volunteer_categories || ['Scoreboard', 'Snacks', 'Field Prep', 'Concessions', 'Parking', 'Photography', 'Other']);
          setDocTypes(s.document_types || ['Physical Exam', 'Liability Waiver', 'Birth Certificate', 'Insurance Card', 'Medical Clearance', 'Other']);
          setSchoolYearStart(s.school_year_start_month || 8);
        }
      } catch {}
    };
    const fetchVenues = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/scheduling/venues/${orgId}`);
        setVenues(data || []);
      } catch {}
    };
    const fetchSports = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/organizations/${orgId}/sports`);
        setSports(data || []);
      } catch {}
    };
    if (orgId) { fetchOrgSettings(); fetchVenues(); fetchSports(); }
  }, [orgId]);

  // Fetch divisions, capacity, refunds, stat config
  useEffect(() => {
    const fetchExtras = async () => {
      try {
        // Fetch league id from org
        const { data: orgs } = await axios.get(`${API_URL}/api/organizations`);
        const org = orgs.find(o => o.id === orgId);
        const lid = org?.league_id || '';
        setLeagueId(lid);
        if (lid) {
          const { data: divs } = await axios.get(`${API_URL}/api/divisions/league/${lid}`);
          setDivisions(divs || []);
        }
      } catch {}
      try {
        const { data: cap } = await axios.get(`${API_URL}/api/registration/capacity/${orgId}`);
        setTeamCapacity(cap || []);
      } catch {}
      try {
        const { data: ref } = await axios.get(`${API_URL}/api/payments/refunds/${orgId}`);
        setRefunds(ref || []);
      } catch {}
      try {
        const { data: sc } = await axios.get(`${API_URL}/api/stats/config/${orgId}`);
        setStatConfig(sc);
      } catch {}
    };
    if (orgId) fetchExtras();
  }, [orgId]);

  const showMsg = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  // ---- Teams & Costs (inline-editable autosave table) -------------------
  // Local mirror of the parent-passed `teams` so inline edits feel
  // instant; we still PUT changes to the server, then merge the response.
  const [teamRows, setTeamRows] = useState(teams);
  useEffect(() => { setTeamRows(teams); }, [teams]);

  // Per-row debounce + in-flight guard so consecutive keystrokes don't
  // spawn parallel PUTs.
  const teamSaveTimers = React.useRef({});
  const teamInFlight = React.useRef({});
  const [teamRowState, setTeamRowState] = useState({}); // {id: {state, error?}}
  const [teamDraft, setTeamDraft] = useState({ sport_id: '', code: '', name: '', season_cost: '0', max_age: '' });
  const [creatingTeam, setCreatingTeam] = useState(false);

  const setTeamSaveStateLocal = (id, state, error) => {
    setTeamRowState(s => ({ ...s, [id]: { state, error } }));
    if (state === 'saved') {
      setTimeout(() => {
        setTeamRowState(s => {
          if (s[id]?.state === 'saved') return { ...s, [id]: { state: 'idle' } };
          return s;
        });
      }, 1800);
    }
  };

  const saveTeamRow = useCallback(async (id, fields) => {
    if (teamInFlight.current[id]) {
      teamSaveTimers.current[id] = setTimeout(() => saveTeamRow(id, fields), 250);
      return;
    }
    teamInFlight.current[id] = true;
    setTeamSaveStateLocal(id, 'saving');
    try {
      const { data } = await axios.put(`${API_URL}/api/teams/${id}`, fields);
      setTeamRows(prev => prev.map(t => (t.id === id ? { ...t, ...data } : t)));
      setTeamSaveStateLocal(id, 'saved');
    } catch (err) {
      setTeamSaveStateLocal(id, 'error', err.response?.data?.detail || 'Save failed');
    } finally {
      teamInFlight.current[id] = false;
    }
  }, []);

  const updateTeamCell = (id, key, value) => {
    let next;
    setTeamRows(prev => prev.map(t => {
      if (t.id !== id) return t;
      // `eligibility.max_age` is a nested patch driven by the Max-Age
      // column. The AD is the source of truth for age eligibility per
      // team — used by Birthdays (Qualifying Team), Roster (eligibility
      // checks), and Family Directory.
      //
      // Session 19 (May 12 2026 v2): we deliberately DO NOT autosave on
      // every keystroke. The old per-keystroke debounce was racing
      // partial values to the server (typing "15" persisted "1" before
      // the second keystroke landed). Instead, the edit is held in
      // local state and `commitTeamCell` (below) fires on input blur,
      // and the per-row "Save" pencil also flushes manually.
      if (key === 'max_age') {
        // Treat the input as a string until commit so we can tell the
        // difference between an in-progress edit ("1" on the way to
        // "15") and a real value.
        const elig = { ...(t.eligibility || {}), max_age: value };
        next = { ...t, eligibility: elig, _dirty: true };
      } else {
        next = { ...t, [key]: value, _dirty: true };
      }
      return next;
    }));
  };

  const commitTeamCell = useCallback((id) => {
    const row = teamRows.find(t => t.id === id);
    if (!row || !row._dirty) return;
    const patch = {
      name: row.name,
      code: row.code,
      season_cost: parseFloat(row.season_cost) || 0,
      eligibility: {
        max_age: parseInt(row.eligibility?.max_age, 10) || 99,
      },
    };
    setTeamRows(prev => prev.map(t => (t.id === id ? { ...t, _dirty: false } : t)));
    saveTeamRow(id, patch);
  }, [teamRows, saveTeamRow]);

  const saveAllDirtyTeams = useCallback(async () => {
    const dirty = teamRows.filter(t => t._dirty);
    if (dirty.length === 0) return;
    // Fire sequentially so the indicator updates accurately.
    for (const row of dirty) {
      const patch = {
        name: row.name,
        code: row.code,
        season_cost: parseFloat(row.season_cost) || 0,
        eligibility: {
          max_age: parseInt(row.eligibility?.max_age, 10) || 99,
        },
      };
      await saveTeamRow(row.id, patch);
    }
    setTeamRows(prev => prev.map(t => ({ ...t, _dirty: false })));
  }, [teamRows, saveTeamRow]);

  const deleteTeamRow = async (team) => {
    if (!window.confirm(`Delete team "${team.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/teams/${team.id}`);
      setTeamRows(prev => prev.filter(t => t.id !== team.id));
      if (teamSaveTimers.current[team.id]) clearTimeout(teamSaveTimers.current[team.id]);
      showMsg('Team deleted');
    } catch (err) { setError(err.response?.data?.detail || 'Delete failed'); }
  };

  const addTeamRow = async () => {
    const f = teamDraft;
    if (!f.sport_id || !f.code.trim() || !f.name.trim()) {
      setError('Sport, code, and name are required to add a team');
      return;
    }
    setCreatingTeam(true); setError('');
    try {
      const payload = {
        org_id: orgId,
        sport_id: f.sport_id,
        name: f.name.trim(),
        code: f.code.trim().toUpperCase(),
        eligibility: { max_age: parseInt(f.max_age, 10) || 99 },
        season_cost: parseFloat(f.season_cost) || 0,
        max_assistants: 3,
      };
      const { data } = await axios.post(`${API_URL}/api/sports/${f.sport_id}/teams`, payload);
      setTeamRows(prev => [...prev, data]);
      setTeamDraft({ sport_id: f.sport_id, code: '', name: '', season_cost: '0', max_age: '' });
      showMsg(`Team "${data.name}" added`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Team creation failed');
    } finally { setCreatingTeam(false); }
  };

  const sportName = (sportId) => sports.find(s => s.id === sportId)?.name || '—';
  const teamFriendlyLabel = (team) => {
    const sn = sportName(team.sport_id);
    return team.code ? `${sn} ${team.code}` : `${sn} ${team.name || ''}`.trim();
  };

  const teamRowStateIcon = (id) => {
    const s = teamRowState[id]?.state || 'idle';
    if (s === 'saving') return <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-500" />;
    if (s === 'saved') return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />;
    if (s === 'error') return <span title={teamRowState[id]?.error || 'Save failed'}><Trash2 className="h-3.5 w-3.5 text-red-500" /></span>;
    return null;
  };

  const saveTeam = async (teamId, updates) => {
    // Legacy: still used by anything that hasn't migrated to inline edit.
    setSaving(true); setError('');
    try {
      await axios.put(`${API_URL}/api/teams/${teamId}`, updates);
      showMsg('Team updated');
      setEditTeam(null);
    } catch (err) { setError(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const addQuestion = async (q) => {
    try {
      await axios.post(`${API_URL}/api/registration/config/${orgId}/question`, q);
      fetchRegConfig(); setShowAddQuestion(false); showMsg('Question added');
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
  };

  const removeQuestion = async (qId) => {
    try {
      await axios.delete(`${API_URL}/api/registration/config/${orgId}/question/${qId}`);
      fetchRegConfig(); showMsg('Question removed');
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
  };

  const toggleRequired = async (qId, currentRequired) => {
    try {
      await axios.put(`${API_URL}/api/registration/config/${orgId}/question/${qId}`, { required: !currentRequired });
      fetchRegConfig();
    } catch (err) { console.error(err); }
  };

  const addDocAgreement = async (doc) => {
    try {
      await axios.post(`${API_URL}/api/registration/config/${orgId}/document`, doc);
      fetchRegConfig(); setShowAddDoc(false); showMsg('Document added');
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
  };

  const removeDoc = async (docId) => {
    try {
      await axios.delete(`${API_URL}/api/registration/config/${orgId}/document/${docId}`);
      fetchRegConfig(); showMsg('Document removed');
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
  };

  const addApprovedTeam = async (team) => {
    try {
      await axios.post(`${API_URL}/api/registration/approved-teams/${orgId}/add`, team);
      fetchApprovedTeams(); setShowAddApproved(false); showMsg('Team approved');
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
  };

  const removeApprovedTeam = async (entryId) => {
    try {
      await axios.delete(`${API_URL}/api/registration/approved-teams/${orgId}/remove/${entryId}`);
      fetchApprovedTeams(); showMsg('Team removed');
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
  };

  const inviteCoach = async (teamId) => {
    setSaving(true); setError('');
    try {
      const { data } = await axios.post(`${API_URL}/api/coaches/invite`, {
        team_id: teamId, ...inviteForm,
      });
      showMsg(data.message || 'Coach invited');
      setShowInviteCoach(null);
      setInviteForm({ email: '', first_name: '', last_name: '', coach_type: 'head' });
      // Refresh coaches for this team
      const { data: refreshed } = await axios.get(`${API_URL}/api/coaches/team/${teamId}`);
      setTeamCoaches(prev => ({ ...prev, [teamId]: refreshed }));
    } catch (err) { setError(err.response?.data?.detail || 'Invite failed'); }
    finally { setSaving(false); }
  };

  const removeCoach = async (teamId, coachUserId) => {
    setSaving(true); setError('');
    try {
      await axios.delete(`${API_URL}/api/coaches/team/${teamId}/coach/${coachUserId}`);
      showMsg('Coach removed');
      const { data: refreshed } = await axios.get(`${API_URL}/api/coaches/team/${teamId}`);
      setTeamCoaches(prev => ({ ...prev, [teamId]: refreshed }));
    } catch (err) { setError(err.response?.data?.detail || 'Remove failed'); }
    finally { setSaving(false); }
  };

  const toggleCrossTeamVisibility = async () => {
    try {
      await axios.put(`${API_URL}/api/organizations/${orgId}`, {
        settings: { coaches_cross_team_visibility: !crossTeamVisibility }
      });
      setCrossTeamVisibility(!crossTeamVisibility);
      showMsg(`Cross-team visibility ${!crossTeamVisibility ? 'enabled' : 'disabled'}`);
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
  };

  const toggleAssistantRestricted = async () => {
    try {
      await axios.put(`${API_URL}/api/organizations/${orgId}`, {
        settings: { assistant_coach_restricted: !assistantRestricted }
      });
      setAssistantRestricted(!assistantRestricted);
      showMsg(`Assistant coach access ${!assistantRestricted ? 'restricted' : 'unrestricted'}`);
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
  };

  const addVenue = async () => {
    if (!venueForm.name) return;
    try {
      await axios.post(`${API_URL}/api/scheduling/venues`, { ...venueForm, org_id: orgId });
      const { data } = await axios.get(`${API_URL}/api/scheduling/venues/${orgId}`);
      setVenues(data || []);
      setVenueForm({ name: '', address: '' }); setShowAddVenue(false); showMsg('Venue added');
    } catch (err) { setError(err.response?.data?.detail || 'Failed to add venue'); }
  };

  const deleteVenue = async (venueId) => {
    try {
      await axios.delete(`${API_URL}/api/scheduling/venues/${venueId}`);
      setVenues(venues.filter(v => v.id !== venueId)); showMsg('Venue deleted');
    } catch (err) { setError(err.response?.data?.detail || 'Failed to delete venue'); }
  };

  const saveDiscounts = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/organizations/${orgId}`, { settings: discountForm });
      showMsg('Discount rules saved');
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const savePaymentSchedule = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/organizations/${orgId}`, { settings: { payment_schedule: scheduleForm } });
      showMsg('Payment schedule saved');
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const saveConfigList = async (key, list) => {
    try {
      await axios.put(`${API_URL}/api/organizations/${orgId}`, { settings: { [key]: list } });
      showMsg('Configuration saved');
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
  };

  const saveSportDates = async (sportId, updates) => {
    try {
      await axios.put(`${API_URL}/api/sports/${sportId}`, updates);
      const { data } = await axios.get(`${API_URL}/api/organizations/${orgId}/sports`);
      setSports(data || []);
      showMsg('Sport dates updated');
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
  };

  const saveSchoolYearStart = async (month) => {
    setSchoolYearStart(month);
    try {
      await axios.put(`${API_URL}/api/organizations/${orgId}`, { settings: { school_year_start_month: month } });
      showMsg('School-year start month updated');
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
  };

  const currentYear = new Date().getFullYear();
  const seasonNumber = orgData?.founding_year ? (currentYear - orgData.founding_year + 1) : null;

  const sections = [
    { key: 'org', label: 'Organization', icon: Building2 },
    { key: 'teams', label: 'Teams & Costs', icon: DollarSign },
    { key: 'cutoff', label: 'Age Cut-off', icon: Clock },
    { key: 'divisions', label: 'Divisions', icon: Layers },
    // Session 19 (May 12 2026):
    //   - 'venues'   → managed in WP Mgmt → Venues (deleted to avoid dual SoT)
    //   - 'capacity' (Roster Capacity) → deleted per user feedback ("makes no sense")
    //   - 'approved' (Approved Teams)  → deleted per user feedback
    //   - 'questions' (Registration form) → top-level "Reg. Builder" tab is canonical
    { key: 'financial', label: 'Financial', icon: Percent },
    { key: 'refunds', label: 'Refunds', icon: RefreshCw },
    { key: 'coaches', label: 'Coaches', icon: UserPlus },
    { key: 'stat_config', label: 'Stat Types', icon: BarChart3 },
    { key: 'config', label: 'Config Lists', icon: Tag },
  ];

  return (
    <div className="space-y-4">
      {/* Section Tabs */}
      <div className="flex gap-2 flex-wrap">
        {sections.map(s => (
          <Button key={s.key} size="sm" variant={activeSection === s.key ? 'default' : 'outline'}
            className={activeSection === s.key ? 'bg-amber-600 hover:bg-amber-700' : isDark ? 'border-slate-600 text-slate-300' : ''}
            onClick={() => setActiveSection(s.key)} data-testid={`settings-tab-${s.key}`}>
            <s.icon className="h-3 w-3 mr-1" />{s.label}
          </Button>
        ))}
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm">{error}</div>}
      {success && <div className="bg-green-500/10 border border-green-500/50 rounded-lg p-3 text-green-400 text-sm flex items-center gap-2"><CheckCircle2 className="h-4 w-4" />{success}</div>}

      {/* AGE CUT-OFF — org-level default (team eligibility overrides) */}
      {activeSection === 'cutoff' && (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
          <CardHeader className="pb-2">
            <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <Clock className="h-5 w-5 text-amber-500" />Age Cut-off Date
            </CardTitle>
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              Organization-wide date used to compute a player's <strong>age at cut-off</strong> and
              qualifying level for registration eligibility, Birthdays, rosters and the family directory.
              This is the single source — there is no per-team cut-off. Leave it unset to skip age checks.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <Label className="text-xs">Month</Label>
                <Input type="number" min="1" max="12" value={orgCutoff.age_cutoff_month ?? ''} placeholder="—"
                  onChange={e => setOrgCutoff(p => ({ ...p, age_cutoff_month: e.target.value }))}
                  className={`h-9 w-24 ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : ''}`}
                  data-testid="org-cutoff-month" />
              </div>
              <div>
                <Label className="text-xs">Day</Label>
                <Input type="number" min="1" max="31" value={orgCutoff.age_cutoff_day ?? ''} placeholder="—"
                  onChange={e => setOrgCutoff(p => ({ ...p, age_cutoff_day: e.target.value }))}
                  className={`h-9 w-24 ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : ''}`}
                  data-testid="org-cutoff-day" />
              </div>
              <Button onClick={saveOrgCutoff} disabled={cutoffSaving || !orgCutoff.age_cutoff_month || !orgCutoff.age_cutoff_day}
                className="h-9 bg-amber-600 hover:bg-amber-700 text-white" data-testid="org-cutoff-save">
                {cutoffSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" />Save</>}
              </Button>
              {cutoffSaved && <span className="text-green-500 text-sm flex items-center gap-1" data-testid="org-cutoff-saved"><CheckCircle2 className="h-4 w-4" />Saved — Birthdays will recompute</span>}
            </div>
            <p className={`text-[11px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`} data-testid="org-cutoff-status">
              {(orgCutoff.age_cutoff_month && orgCutoff.age_cutoff_day) ? (
                <>Current cut-off: <strong>{orgCutoff.age_cutoff_month}/{orgCutoff.age_cutoff_day}</strong>. Example: 8/1 counts a player's age as of August 1.</>
              ) : (
                <span className="text-amber-500">Not set — age checks are skipped until you set a cut-off.</span>
              )}
            </p>
          </CardContent>
        </Card>
      )}

      {/* TEAMS & COSTS — inline-editable autosave table */}
      {activeSection === 'teams' && (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  <DollarSign className="h-5 w-5 text-amber-500" />Teams &amp; Costs
                </CardTitle>
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  One row per team. Friendly labels ("Basketball VB") are derived from sport + code so the public site, stats engine, and Staff dropdowns all stay aligned. Edits commit when you leave a cell or click <strong>Save changes</strong>.
                </p>
              </div>
              {(() => {
                const dirtyCount = teamRows.filter(t => t._dirty).length;
                return (
                  <Button
                    onClick={saveAllDirtyTeams}
                    disabled={dirtyCount === 0}
                    className={dirtyCount > 0
                      ? 'bg-amber-600 hover:bg-amber-700 text-white'
                      : 'bg-slate-700/40 text-slate-400 cursor-not-allowed'}
                    data-testid="teams-save-all-btn"
                  >
                    <Save className="h-4 w-4 mr-1" />
                    {dirtyCount > 0 ? `Save changes (${dirtyCount})` : 'All saved'}
                  </Button>
                );
              })()}
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="teams-costs-table">
                <thead>
                  <tr className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                    <th className={`text-left py-2 px-2 text-[11px] font-semibold uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Sport</th>
                    <th className={`text-left py-2 px-2 text-[11px] font-semibold uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Code</th>
                    <th className={`text-left py-2 px-2 text-[11px] font-semibold uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Display name</th>
                    <th className={`text-left py-2 px-2 text-[11px] font-semibold uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Friendly label</th>
                    <th className={`text-left py-2 px-2 text-[11px] font-semibold uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-500'}`} title="Maximum age allowed at the org Age Cut-off (Settings ▸ Age Cut-off). Used by Birthdays, Roster, and Family Directory to compute eligibility.">Max age</th>
                    <th className={`text-left py-2 px-2 text-[11px] font-semibold uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Season cost ($)</th>
                    <th className={`text-right py-2 px-2 text-[11px] font-semibold uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  {teamRows.length === 0 && (
                    <tr><td colSpan={7} className={`py-6 text-center text-xs italic ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      No teams yet — fill in the row below to add the first one.
                    </td></tr>
                  )}
                  {teamRows.map(team => (
                    <tr key={team.id}
                        className={`border-b ${isDark ? 'border-slate-700/40 hover:bg-slate-700/20' : 'border-slate-200/50 hover:bg-amber-50/30'}`}
                        data-testid={`team-row-${team.id}`}>
                      <td className="py-1.5 px-2 align-middle">
                        <span className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                          {sportName(team.sport_id)}
                        </span>
                      </td>
                      <td className="py-1.5 px-2 align-middle">
                        <Input value={team.code || ''}
                               onChange={(e) => updateTeamCell(team.id, 'code', e.target.value.toUpperCase())}
                               onBlur={() => commitTeamCell(team.id)}
                               className={`h-8 text-xs uppercase ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : ''} ${team._dirty ? 'ring-1 ring-amber-500/40' : ''}`}
                               data-testid={`team-cell-code-${team.id}`} />
                      </td>
                      <td className="py-1.5 px-2 align-middle">
                        <Input value={team.name || ''}
                               onChange={(e) => updateTeamCell(team.id, 'name', e.target.value)}
                               onBlur={() => commitTeamCell(team.id)}
                               className={`h-8 text-xs ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : ''} ${team._dirty ? 'ring-1 ring-amber-500/40' : ''}`}
                               data-testid={`team-cell-name-${team.id}`} />
                      </td>
                      <td className="py-1.5 px-2 align-middle">
                        <span className={`text-xs font-mono ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                          {teamFriendlyLabel(team)}
                        </span>
                      </td>
                      <td className="py-1.5 px-2 align-middle">
                        <Input
                          type="number" min="1" max="99"
                          value={team.eligibility?.max_age ?? ''}
                          placeholder="age"
                          onChange={(e) => updateTeamCell(team.id, 'max_age', e.target.value)}
                          onBlur={() => commitTeamCell(team.id)}
                          className={`h-8 text-xs w-20 ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : ''} ${team._dirty ? 'ring-1 ring-amber-500/40' : ''}`}
                          data-testid={`team-cell-maxage-${team.id}`}
                        />
                      </td>
                      <td className="py-1.5 px-2 align-middle">
                        <Input type="number" min="0" step="0.01" value={team.season_cost ?? 0}
                               onChange={(e) => updateTeamCell(team.id, 'season_cost', e.target.value)}
                               onBlur={() => commitTeamCell(team.id)}
                               className={`h-8 text-xs w-28 ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : ''} ${team._dirty ? 'ring-1 ring-amber-500/40' : ''}`}
                               data-testid={`team-cell-cost-${team.id}`} />
                      </td>
                      <td className="py-1.5 px-2 text-right align-middle">
                        <div className="flex items-center justify-end gap-1.5">
                          {teamRowStateIcon(team.id)}
                          <Button size="sm" variant="ghost"
                                  onClick={() => deleteTeamRow(team)}
                                  className={`h-7 px-2 gap-1 ${isDark ? 'text-red-400 hover:bg-red-500/10' : 'text-red-600 hover:bg-red-50'}`}
                                  data-testid={`team-del-${team.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                            <span className="text-[10px] font-medium">Delete</span>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {/* Add-team row */}
                  <tr className={isDark ? 'bg-amber-900/15' : 'bg-amber-50/40'} data-testid="team-add-row">
                    <td className="py-2 px-2">
                      <select value={teamDraft.sport_id}
                              onChange={(e) => setTeamDraft(p => ({ ...p, sport_id: e.target.value }))}
                              className={`h-8 text-xs rounded px-2 border w-full ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                              data-testid="team-add-sport">
                        <option value="">Sport…</option>
                        {sports.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </td>
                    <td className="py-2 px-2">
                      <Input value={teamDraft.code}
                             onChange={(e) => setTeamDraft(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                             placeholder="VB"
                             className={`h-8 text-xs uppercase ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : ''}`}
                             data-testid="team-add-code" />
                    </td>
                    <td className="py-2 px-2">
                      <Input value={teamDraft.name}
                             onChange={(e) => setTeamDraft(p => ({ ...p, name: e.target.value }))}
                             placeholder="Varsity Boys"
                             className={`h-8 text-xs ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : ''}`}
                             data-testid="team-add-name" />
                    </td>
                    <td className="py-2 px-2 text-xs italic">
                      <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>auto-derived</span>
                    </td>
                    <td className="py-2 px-2">
                      <Input
                        type="number" min="1" max="99"
                        value={teamDraft.max_age}
                        onChange={(e) => setTeamDraft(p => ({ ...p, max_age: e.target.value }))}
                        placeholder="age"
                        className={`h-8 text-xs w-20 ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : ''}`}
                        data-testid="team-add-maxage"
                      />
                    </td>
                    <td className="py-2 px-2">
                      <Input type="number" min="0" step="0.01" value={teamDraft.season_cost}
                             onChange={(e) => setTeamDraft(p => ({ ...p, season_cost: e.target.value }))}
                             placeholder="0"
                             className={`h-8 text-xs w-28 ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : ''}`}
                             data-testid="team-add-cost" />
                    </td>
                    <td className="py-2 px-2 text-right">
                      <Button size="sm" onClick={addTeamRow}
                              disabled={creatingTeam || !teamDraft.sport_id || !teamDraft.code.trim() || !teamDraft.name.trim()}
                              className="h-7 px-3 bg-amber-600 hover:bg-amber-700 text-white"
                              data-testid="team-add-btn">
                        {creatingTeam ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="h-3.5 w-3.5 mr-1" />Add</>}
                      </Button>
                    </td>
                  </tr>
                </tbody>
              </table>
              <p className={`mt-3 text-[11px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                <Mail className="h-3 w-3 inline mr-1" />
                These teams show up in the Organization → Staff "Team" dropdown as Friendly labels. Game duration and age cutoff have moved to per-game configuration so this page stays focused on what's billed and rostered.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* COACHES MANAGEMENT */}
      {activeSection === 'coaches' && (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
          <CardHeader className="pb-2">
            <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <UserPlus className="h-5 w-5 text-green-500" />Coach Assignments
            </CardTitle>
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              Assign Head Coach and up to N Assistant Coaches per team. Invite via email link.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Cross-team visibility toggle */}
            <div className={`flex items-center justify-between p-3 rounded-lg border ${isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
              <div>
                <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Cross-Team Visibility</p>
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Allow coaches to view other teams' rosters and schedules</p>
              </div>
              <Button size="sm" variant={crossTeamVisibility ? 'default' : 'outline'}
                className={crossTeamVisibility ? 'bg-green-600 hover:bg-green-700' : isDark ? 'border-slate-600 text-slate-300' : ''}
                onClick={toggleCrossTeamVisibility} data-testid="cross-team-visibility-toggle">
                {crossTeamVisibility ? 'Enabled' : 'Disabled'}
              </Button>
            </div>

            {/* Assistant Coach Restriction toggle */}
            <div className={`flex items-center justify-between p-3 rounded-lg border ${isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
              <div>
                <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Restrict Assistant Coaches</p>
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Limit assistant coaches to roster view only (no schedule or payment access)</p>
              </div>
              <Button size="sm" variant={assistantRestricted ? 'default' : 'outline'}
                className={assistantRestricted ? 'bg-amber-600 hover:bg-amber-700' : isDark ? 'border-slate-600 text-slate-300' : ''}
                onClick={toggleAssistantRestricted} data-testid="assistant-coach-restricted-toggle">
                {assistantRestricted ? 'Restricted' : 'Unrestricted'}
              </Button>
            </div>

            {/* Per-team coach cards */}
            {teams.map(team => {
              const tc = teamCoaches[team.id] || { coaches: [], max_assistants: 2 };
              const headCoach = tc.coaches?.find(c => c.coach_type === 'head');
              const assistants = tc.coaches?.filter(c => c.coach_type === 'assistant') || [];
              const maxAsst = tc.max_assistants || team.max_assistants || 2;

              return (
                <div key={team.id} className={`p-4 rounded-lg border ${isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-slate-50 border-slate-200'}`} data-testid={`coach-config-${team.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{team.name}</span>
                      <Badge className="ml-2 bg-green-600 text-xs">{team.code}</Badge>
                    </div>
                    <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => { setShowInviteCoach(team.id); setInviteForm({ email: '', first_name: '', last_name: '', coach_type: headCoach ? 'assistant' : 'head' }); }}
                      data-testid={`invite-coach-${team.id}-btn`}>
                      <Mail className="h-3 w-3 mr-1" />Invite Coach
                    </Button>
                  </div>

                  {/* Head Coach */}
                  <div className={`flex items-center justify-between px-3 py-2 rounded mb-1 ${isDark ? 'bg-slate-600/30' : 'bg-white'}`}>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-amber-600 text-xs">HC</Badge>
                      {headCoach ? (
                        <span className={`text-sm ${isDark ? 'text-white' : 'text-slate-800'}`}>
                          {headCoach.first_name} {headCoach.last_name}
                          <span className={`ml-1 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>({headCoach.email})</span>
                        </span>
                      ) : (
                        <span className={`text-sm italic ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>No head coach assigned</span>
                      )}
                    </div>
                    {headCoach?.id && (
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400" onClick={() => removeCoach(team.id, headCoach.id)} data-testid={`remove-hc-${team.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>

                  {/* Assistants */}
                  <p className={`text-xs mt-2 mb-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Assistants ({assistants.length}/{maxAsst})</p>
                  {assistants.map(a => (
                    <div key={a.id || a.email} className={`flex items-center justify-between px-3 py-1.5 rounded mb-1 ${isDark ? 'bg-slate-600/20' : 'bg-slate-50'}`}>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-slate-600 text-xs">AC</Badge>
                        <span className={`text-sm ${isDark ? 'text-white' : 'text-slate-800'}`}>
                          {a.first_name} {a.last_name}
                          <span className={`ml-1 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>({a.email})</span>
                        </span>
                      </div>
                      {a.id && (
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400" onClick={() => removeCoach(team.id, a.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                  {assistants.length === 0 && <p className={`text-xs italic px-3 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>No assistant coaches</p>}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* REGISTRATION QUESTIONS */}
      {activeSection === 'questions' && regConfig && (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
                <FileText className="h-5 w-5 text-blue-500" />Registration Questions
              </CardTitle>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Configure form fields, ordering, and conditional follow-ups</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setShowAddDoc(true)} className={isDark ? 'border-slate-600 text-slate-300' : ''} data-testid="add-doc-btn">
                <FileText className="h-3 w-3 mr-1" />Add Doc
              </Button>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => setShowAddQuestion(true)} data-testid="add-question-btn">
                <Plus className="h-4 w-4 mr-1" />Add Question
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Document Agreements */}
            {regConfig.document_agreements?.length > 0 && (
              <div className="mb-4">
                <p className={`text-xs font-medium mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Document Agreements:</p>
                {regConfig.document_agreements.map(doc => (
                  <div key={doc.id} className={`flex items-center justify-between px-3 py-2 rounded-lg mb-1 ${isDark ? 'bg-slate-700/30' : 'bg-slate-50'}`}>
                    <div className="flex items-center gap-2">
                      <FileText className="h-3 w-3 text-blue-400" />
                      <span className={`text-sm ${isDark ? 'text-white' : 'text-slate-800'}`}>{doc.title}</span>
                      {doc.required && <Badge className="bg-red-600">Required</Badge>}
                    </div>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400" onClick={() => removeDoc(doc.id)}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                ))}
              </div>
            )}
            {/* Questions */}
            <div className="space-y-2">
              {(regConfig.questions || []).sort((a, b) => (a.order || 0) - (b.order || 0)).map(q => (
                <div key={q.id} className={`flex items-center justify-between px-3 py-3 rounded-lg ${isDark ? 'bg-slate-700/30' : 'bg-slate-50'}`} data-testid={`question-${q.id}`}>
                  <div className="flex items-center gap-3">
                    <GripVertical className={`h-4 w-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                    <div>
                      <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>{q.label}</p>
                      <div className="flex gap-2 mt-0.5">
                        <Badge className={isDark ? 'bg-slate-600' : 'bg-slate-200'}>{q.type}</Badge>
                        <Badge className={isDark ? 'bg-slate-600' : 'bg-slate-200'}>{q.section}</Badge>
                        {q.conditional && <Badge className="bg-purple-600">Conditional</Badge>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant={q.required ? "default" : "outline"} className={`h-7 text-xs ${q.required ? 'bg-red-600' : isDark ? 'border-slate-600 text-slate-400' : ''}`} onClick={() => toggleRequired(q.id, q.required)}>
                      {q.required ? 'Required' : 'Optional'}
                    </Button>
                    {!q.id.startsWith('q-') && (
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400" onClick={() => removeQuestion(q.id)}><Trash2 className="h-3 w-3" /></Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* APPROVED TEAMS */}
      {activeSection === 'approved' && (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
                <Shield className="h-5 w-5 text-green-500" />Approved Scheduling Teams
              </CardTitle>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Only approved teams can schedule games with your organization</p>
            </div>
            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => setShowAddApproved(true)} data-testid="add-approved-btn">
              <Plus className="h-4 w-4 mr-1" />Add Team
            </Button>
          </CardHeader>
          <CardContent>
            {(!approvedTeams?.approved_teams || approvedTeams.approved_teams.length === 0) ? (
              <div className={`text-center py-6 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                <Shield className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>No approved teams yet</p>
                <p className="text-xs">Add teams to allow them to schedule games</p>
              </div>
            ) : (
              <div className="space-y-2">
                {approvedTeams.approved_teams.map(t => (
                  <div key={t.id} className={`flex items-center justify-between px-3 py-3 rounded-lg ${isDark ? 'bg-slate-700/30' : 'bg-slate-50'}`} data-testid={`approved-${t.id}`}>
                    <div>
                      <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-800'}`}>{t.org_name}</p>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{t.team_name} · {t.contact_email || 'No email'}</p>
                    </div>
                    <Button size="sm" variant="ghost" className="text-red-400" onClick={() => removeApprovedTeam(t.id)} data-testid={`remove-approved-${t.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ORGANIZATION INFO */}
      {activeSection === 'org' && orgData && (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
          <CardHeader className="pb-2">
            <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <Building2 className="h-5 w-5 text-blue-500" />Organization Info
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className={`p-4 rounded-lg border ${isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Name</p>
                  <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{orgData.name}</p>
                </div>
                <div>
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Founding Year</p>
                  <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{orgData.founding_year}</p>
                </div>
                <div>
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Season Number</p>
                  <p className={`text-sm font-medium text-amber-500`}>{seasonNumber ? `${seasonNumber}th Season` : 'N/A'}</p>
                </div>
                <div>
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>League Division</p>
                  <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{(orgData.divisions || []).join(', ') || 'None'}</p>
                </div>
              </div>
            </div>
            {/* School-year start month — the ONE current-season flip control */}
            <div className="mb-4" data-testid="school-year-start-block">
              <div className="flex items-center gap-2 mb-1">
                <p className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>School-Year Start Month</p>
                <span
                  title="This ONE setting decides WHEN the current season flips for EVERY sport (school-year calendar). E.g. August means seasons run Aug–Jul and the site shows the new season starting each August. The per-sport 'Sport Season Dates' below do NOT control the flip — they only label spring vs fall seasons."
                  className={`cursor-help text-xs rounded-full w-4 h-4 inline-flex items-center justify-center border ${isDark ? 'border-slate-500 text-slate-400' : 'border-slate-400 text-slate-500'}`}
                  data-testid="school-year-start-tooltip">?</span>
              </div>
              <select
                value={schoolYearStart}
                onChange={e => saveSchoolYearStart(parseInt(e.target.value))}
                data-testid="school-year-start-select"
                className={`h-9 rounded px-2 text-sm border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`}>
                {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, idx) => (
                  <option key={idx} value={idx + 1}>{m}</option>
                ))}
              </select>
            </div>
            {/* Sport Season Dates */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <p className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Sport Season Dates</p>
                <span
                  title="Per-sport start/end months are used ONLY for academic labeling — a start month of 6 or earlier marks a SPRING sport (e.g. baseball), so its season is labeled with the prior academic year. These do NOT control when the current season flips; that is the School-Year Start Month above."
                  className={`cursor-help text-xs rounded-full w-4 h-4 inline-flex items-center justify-center border ${isDark ? 'border-slate-500 text-slate-400' : 'border-slate-400 text-slate-500'}`}
                  data-testid="sport-dates-tooltip">?</span>
              </div>
              {sports.map(sport => (
                <SportDateEditor key={sport.id} sport={sport} onSave={saveSportDates} isDark={isDark} />
              ))}
              {sports.length === 0 && <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>No sports configured</p>}
            </div>
          </CardContent>
        </Card>
      )}

      {/* VENUES */}
      {activeSection === 'venues' && (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <MapPin className="h-5 w-5 text-emerald-500" />Venue Management
            </CardTitle>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setShowAddVenue(true)} data-testid="add-venue-btn">
              <Plus className="h-4 w-4 mr-1" />Add Venue
            </Button>
          </CardHeader>
          <CardContent>
            {venues.length === 0 ? (
              <div className={`text-center py-6 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                <MapPin className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>No venues configured</p>
                <p className="text-xs">Add venues for scheduling games</p>
              </div>
            ) : (
              <div className="space-y-2">
                {venues.map(v => (
                  <div key={v.id} className={`flex items-center justify-between px-4 py-3 rounded-lg ${isDark ? 'bg-slate-700/30' : 'bg-slate-50'}`} data-testid={`venue-${v.id}`}>
                    <div>
                      <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-800'}`}>{v.name}</p>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{v.address || 'No address'}</p>
                      {v.blocked_dates?.length > 0 && (
                        <Badge className="mt-1 bg-red-600 text-xs">{v.blocked_dates.length} blocked dates</Badge>
                      )}
                    </div>
                    <Button size="sm" variant="ghost" className="text-red-400" onClick={() => deleteVenue(v.id)} data-testid={`delete-venue-${v.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* FINANCIAL — Discounts & Payment Schedule */}
      {activeSection === 'financial' && (
        <div className="space-y-4">
          <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
            <CardHeader className="pb-2">
              <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
                <Percent className="h-5 w-5 text-green-500" />Discount Rules
              </CardTitle>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Configure automatic discounts applied during checkout</p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Multi-Child Discount (%)</Label>
                  <Input type="number" min="0" max="100" value={discountForm.multi_child_discount_pct}
                    onChange={e => setDiscountForm({...discountForm, multi_child_discount_pct: parseFloat(e.target.value) || 0})}
                    className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="discount-multi-child" />
                </div>
                <div>
                  <Label className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Coach Parent Discount (%)</Label>
                  <Input type="number" min="0" max="100" value={discountForm.coach_parent_discount_pct}
                    onChange={e => setDiscountForm({...discountForm, coach_parent_discount_pct: parseFloat(e.target.value) || 0})}
                    className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="discount-coach" />
                </div>
                <div>
                  <Label className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Scholarship Discount (%)</Label>
                  <Input type="number" min="0" max="100" value={discountForm.scholarship_discount_pct}
                    onChange={e => setDiscountForm({...discountForm, scholarship_discount_pct: parseFloat(e.target.value) || 0})}
                    className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="discount-scholarship" />
                </div>
                <div>
                  <Label className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Early Bird Discount (%)</Label>
                  <Input type="number" min="0" max="100" value={discountForm.early_bird_discount_pct}
                    onChange={e => setDiscountForm({...discountForm, early_bird_discount_pct: parseFloat(e.target.value) || 0})}
                    className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="discount-early-bird" />
                </div>
              </div>
              {discountForm.early_bird_discount_pct > 0 && (
                <div>
                  <Label className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Early Bird Deadline</Label>
                  <Input type="date" value={discountForm.early_bird_deadline}
                    onChange={e => setDiscountForm({...discountForm, early_bird_deadline: e.target.value})}
                    className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="discount-deadline" />
                </div>
              )}
              <Button className="bg-green-600 hover:bg-green-700" onClick={saveDiscounts} disabled={saving} data-testid="save-discounts-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-3 w-3 mr-1" />Save Discounts</>}
              </Button>
            </CardContent>
          </Card>

          <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
            <CardHeader className="pb-2">
              <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
                <Clock className="h-5 w-5 text-blue-500" />Payment Schedule
              </CardTitle>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Configure installment payment plans for families</p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <input type="checkbox" id="sched-enabled" checked={scheduleForm.enabled}
                  onChange={e => setScheduleForm({...scheduleForm, enabled: e.target.checked})} />
                <Label htmlFor="sched-enabled" className={isDark ? 'text-slate-300' : ''}>Enable installment payments</Label>
              </div>
              {scheduleForm.enabled && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Number of Installments</Label>
                    <Input type="number" min="2" max="12" value={scheduleForm.installments}
                      onChange={e => setScheduleForm({...scheduleForm, installments: parseInt(e.target.value) || 2})}
                      className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="sched-installments" />
                  </div>
                  <div>
                    <Label className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Days Between Installments</Label>
                    <Input type="number" min="7" max="90" value={scheduleForm.interval_days}
                      onChange={e => setScheduleForm({...scheduleForm, interval_days: parseInt(e.target.value) || 30})}
                      className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="sched-interval" />
                  </div>
                </div>
              )}
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={savePaymentSchedule} disabled={saving} data-testid="save-schedule-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-3 w-3 mr-1" />Save Schedule</>}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* CONFIG LISTS — Volunteer Categories & Document Types */}
      {activeSection === 'config' && (
        <div className="space-y-4">
          <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
            <CardHeader className="pb-2">
              <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
                <Tag className="h-5 w-5 text-purple-500" />Volunteer Categories
              </CardTitle>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Configurable categories for volunteer signup</p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {volCategories.map((cat, idx) => (
                  <Badge key={idx} className={`${isDark ? 'bg-slate-600' : 'bg-slate-200 text-slate-800'} flex items-center gap-1`}>
                    {cat}
                    <button onClick={() => { const updated = volCategories.filter((_, i) => i !== idx); setVolCategories(updated); saveConfigList('volunteer_categories', updated); }}
                      className="ml-1 hover:text-red-400"><Trash2 className="h-2.5 w-2.5" /></button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={newCategory} onChange={e => setNewCategory(e.target.value)} placeholder="New category..."
                  className={`flex-1 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}`} data-testid="new-vol-category"
                  onKeyDown={e => { if (e.key === 'Enter' && newCategory.trim()) { const updated = [...volCategories, newCategory.trim()]; setVolCategories(updated); saveConfigList('volunteer_categories', updated); setNewCategory(''); }}} />
                <Button size="sm" onClick={() => { if (newCategory.trim()) { const updated = [...volCategories, newCategory.trim()]; setVolCategories(updated); saveConfigList('volunteer_categories', updated); setNewCategory(''); }}}
                  className="bg-purple-600 hover:bg-purple-700" data-testid="add-vol-category-btn"><Plus className="h-4 w-4" /></Button>
              </div>
            </CardContent>
          </Card>

          <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
            <CardHeader className="pb-2">
              <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
                <FileText className="h-5 w-5 text-cyan-500" />Document Types
              </CardTitle>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Configurable document types for player records</p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {docTypes.map((dtype, idx) => (
                  <Badge key={idx} className={`${isDark ? 'bg-slate-600' : 'bg-slate-200 text-slate-800'} flex items-center gap-1`}>
                    {dtype}
                    <button onClick={() => { const updated = docTypes.filter((_, i) => i !== idx); setDocTypes(updated); saveConfigList('document_types', updated); }}
                      className="ml-1 hover:text-red-400"><Trash2 className="h-2.5 w-2.5" /></button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={newDocType} onChange={e => setNewDocType(e.target.value)} placeholder="New document type..."
                  className={`flex-1 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}`} data-testid="new-doc-type"
                  onKeyDown={e => { if (e.key === 'Enter' && newDocType.trim()) { const updated = [...docTypes, newDocType.trim()]; setDocTypes(updated); saveConfigList('document_types', updated); setNewDocType(''); }}} />
                <Button size="sm" onClick={() => { if (newDocType.trim()) { const updated = [...docTypes, newDocType.trim()]; setDocTypes(updated); saveConfigList('document_types', updated); setNewDocType(''); }}}
                  className="bg-cyan-600 hover:bg-cyan-700" data-testid="add-doc-type-btn"><Plus className="h-4 w-4" /></Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add Question Dialog */}
      <QuestionDialog open={showAddQuestion} onClose={() => setShowAddQuestion(false)} onSave={addQuestion} isDark={isDark} questions={regConfig?.questions || []} />

      {/* Add Document Dialog */}
      <DocDialog open={showAddDoc} onClose={() => setShowAddDoc(false)} onSave={addDocAgreement} isDark={isDark} />

      {/* Add Approved Team Dialog */}
      <ApprovedTeamDialog open={showAddApproved} onClose={() => setShowAddApproved(false)} onSave={addApprovedTeam} isDark={isDark} />

      {/* Add Venue Dialog */}
      <Dialog open={showAddVenue} onOpenChange={setShowAddVenue}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
          <DialogHeader><DialogTitle className={isDark ? 'text-white' : 'text-slate-900'}>Add Venue</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className={isDark ? 'text-slate-300' : ''}>Venue Name *</Label>
              <Input value={venueForm.name} onChange={e => setVenueForm({...venueForm, name: e.target.value})}
                placeholder="e.g. Pleasant Grove Baptist" className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="venue-name" /></div>
            <div><Label className={isDark ? 'text-slate-300' : ''}>Address</Label>
              <Input value={venueForm.address} onChange={e => setVenueForm({...venueForm, address: e.target.value})}
                placeholder="123 Main St, Apex NC" className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="venue-address" /></div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowAddVenue(false)}>Cancel</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={addVenue} disabled={!venueForm.name} data-testid="save-venue-btn">
                <Save className="h-3 w-3 mr-1" />Save Venue
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite Coach Dialog */}
      <Dialog open={!!showInviteCoach} onOpenChange={() => setShowInviteCoach(null)}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-slate-900'}>
              Invite Coach — {teams.find(t => t.id === showInviteCoach)?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>Email Address *</Label>
              <Input type="email" value={inviteForm.email} onChange={e => setInviteForm({...inviteForm, email: e.target.value})} placeholder="coach@example.com"
                className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="invite-email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className={isDark ? 'text-slate-300' : ''}>First Name</Label>
                <Input value={inviteForm.first_name} onChange={e => setInviteForm({...inviteForm, first_name: e.target.value})} placeholder="John"
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="invite-first-name" />
              </div>
              <div>
                <Label className={isDark ? 'text-slate-300' : ''}>Last Name</Label>
                <Input value={inviteForm.last_name} onChange={e => setInviteForm({...inviteForm, last_name: e.target.value})} placeholder="Doe"
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="invite-last-name" />
              </div>
            </div>
            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>Role</Label>
              <select value={inviteForm.coach_type} onChange={e => setInviteForm({...inviteForm, coach_type: e.target.value})}
                className={`w-full h-10 rounded-md px-3 border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}`} data-testid="invite-coach-type">
                <option value="head">Head Coach</option>
                <option value="assistant">Assistant Coach</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowInviteCoach(null)}>Cancel</Button>
              <Button className="bg-green-600 hover:bg-green-700" onClick={() => inviteCoach(showInviteCoach)} disabled={!inviteForm.email || saving} data-testid="send-invite-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-3 w-3 mr-1" />Send Invite</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* PUBLISH TRIGGERS + API INTERFACES moved to TechSettingsPanel (Tech Specialist tab) */}

      {/* DIVISIONS MANAGEMENT */}
      {activeSection === 'divisions' && (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  <Layers className="h-5 w-5 text-purple-500" />Division Management
                </CardTitle>
                <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Create and manage league divisions, assign organizations</p>
              </div>
              <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={() => setShowAddDivision(true)} data-testid="add-division-btn">
                <Plus className="h-4 w-4 mr-1" />Add Division
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {divisions.length === 0 ? (
              <div className={`text-center py-8 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                <Layers className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No divisions configured. Create your first division above.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {divisions.map(div => (
                  <div key={div.id} className={`p-4 rounded-lg border ${isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-slate-50 border-slate-200'}`} data-testid={`division-${div.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{div.name}</span>
                        {div.region && <Badge variant="outline" className="ml-2 text-xs">{div.region}</Badge>}
                        {div.max_teams > 0 && <Badge variant="outline" className="ml-2 text-xs">Max {div.max_teams} teams</Badge>}
                        <Badge className="ml-2 bg-purple-600/20 text-purple-400 text-xs">{(div.org_ids || []).length} orgs</Badge>
                      </div>
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-400" onClick={async () => {
                        try {
                          await axios.delete(`${API_URL}/api/divisions/${div.id}`);
                          setDivisions(divisions.filter(d => d.id !== div.id));
                          showMsg('Division deleted');
                        } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
                      }} data-testid={`delete-division-${div.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    {div.description && <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{div.description}</p>}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ADD DIVISION DIALOG */}
      <Dialog open={showAddDivision} onOpenChange={setShowAddDivision}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700 text-white' : ''}>
          <DialogHeader><DialogTitle className={isDark ? 'text-white' : ''}>Add New Division</DialogTitle></DialogHeader>
          <AddDivisionForm leagueId={leagueId} isDark={isDark} onSave={async (div) => {
            try {
              const { data } = await axios.post(`${API_URL}/api/divisions/league/${leagueId}`, div);
              setDivisions([...divisions, data]);
              setShowAddDivision(false);
              showMsg('Division created');
            } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
          }} onClose={() => setShowAddDivision(false)} />
        </DialogContent>
      </Dialog>

      {/* ROSTER CAPACITY */}
      {activeSection === 'capacity' && (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
          <CardHeader className="pb-2">
            <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <ListChecks className="h-5 w-5 text-teal-500" />Roster Capacity & Waitlists
            </CardTitle>
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Set maximum roster size per team. Full teams auto-enable waitlists.</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {teamCapacity.map(tc => (
                <div key={tc.team_id} className={`p-4 rounded-lg border ${isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-slate-50 border-slate-200'}`} data-testid={`capacity-${tc.team_id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{tc.team_name}</span>
                      <div className={`flex gap-3 mt-1 text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                        <span>Roster: {tc.current_roster}{tc.max_roster_size > 0 ? `/${tc.max_roster_size}` : ' (unlimited)'}</span>
                        {tc.is_full && <Badge className="bg-red-500/20 text-red-400 text-xs">Full</Badge>}
                        {tc.waitlist_count > 0 && <Badge className="bg-amber-500/20 text-amber-400 text-xs">{tc.waitlist_count} on waitlist</Badge>}
                        {tc.spots_remaining > 0 && <Badge className="bg-green-500/20 text-green-400 text-xs">{tc.spots_remaining} spots left</Badge>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input type="number" min="0" defaultValue={tc.max_roster_size} className={`w-20 h-8 text-sm ${isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}`}
                        onBlur={async (e) => {
                          const val = parseInt(e.target.value) || 0;
                          try {
                            await axios.put(`${API_URL}/api/registration/capacity/${orgId}/${tc.team_id}`, { max_roster_size: val });
                            showMsg(`${tc.team_name} capacity set to ${val || 'unlimited'}`);
                            const { data } = await axios.get(`${API_URL}/api/registration/capacity/${orgId}`);
                            setTeamCapacity(data || []);
                          } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
                        }} data-testid={`capacity-input-${tc.team_id}`} />
                      <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>max</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* REFUNDS */}
      {activeSection === 'refunds' && (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
          <CardHeader className="pb-2">
            <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <RefreshCw className="h-5 w-5 text-orange-500" />Refund Processing
            </CardTitle>
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Review and process refund requests from families</p>
          </CardHeader>
          <CardContent>
            {refunds.length === 0 ? (
              <div className={`text-center py-8 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                <RefreshCw className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No refund requests pending.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {refunds.map(r => (
                  <div key={r.id} className={`p-4 rounded-lg border ${isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-slate-50 border-slate-200'}`} data-testid={`refund-${r.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{r.family_name}</span>
                        <div className={`text-sm mt-1 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                          <span>Amount: ${r.refund_amount?.toFixed(2) || '0.00'} of ${r.original_amount?.toFixed(2) || '0.00'}</span>
                          {r.reason && <span className="ml-3">Reason: {r.reason}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={
                          r.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                          r.status === 'approved' ? 'bg-blue-500/20 text-blue-400' :
                          r.status === 'processed' ? 'bg-green-500/20 text-green-400' :
                          'bg-red-500/20 text-red-400'
                        }>{r.status}</Badge>
                        {r.status === 'pending' && (
                          <>
                            <Button size="sm" className="bg-green-600 hover:bg-green-700 h-7 px-2" onClick={async () => {
                              try {
                                await axios.put(`${API_URL}/api/payments/refund/${r.id}`, { action: 'approve' });
                                const { data } = await axios.get(`${API_URL}/api/payments/refunds/${orgId}`);
                                setRefunds(data || []);
                                showMsg('Refund approved');
                              } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
                            }} data-testid={`approve-refund-${r.id}`}>Approve</Button>
                            <Button size="sm" variant="outline" className="h-7 px-2 text-red-500 border-red-500/30" onClick={async () => {
                              try {
                                await axios.put(`${API_URL}/api/payments/refund/${r.id}`, { action: 'reject' });
                                const { data } = await axios.get(`${API_URL}/api/payments/refunds/${orgId}`);
                                setRefunds(data || []);
                                showMsg('Refund rejected');
                              } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
                            }} data-testid={`reject-refund-${r.id}`}>Reject</Button>
                          </>
                        )}
                        {r.status === 'approved' && (
                          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 h-7 px-2" onClick={async () => {
                            try {
                              await axios.put(`${API_URL}/api/payments/refund/${r.id}`, { action: 'process' });
                              const { data } = await axios.get(`${API_URL}/api/payments/refunds/${orgId}`);
                              setRefunds(data || []);
                              showMsg('Refund processed');
                            } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
                          }} data-testid={`process-refund-${r.id}`}>Process Refund</Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* STAT TYPES CONFIG */}
      {activeSection === 'stat_config' && (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
          <CardHeader className="pb-2">
            <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <BarChart3 className="h-5 w-5 text-blue-500" />Custom Stat Types per Sport
            </CardTitle>
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Configure tracked statistical categories for each sport. Abbreviations shown in standings tables.</p>
          </CardHeader>
          <CardContent>
            {statConfig?.sport_stats ? Object.entries(statConfig.sport_stats).map(([sport, data]) => (
              <div key={sport} className={`p-4 mb-3 rounded-lg border ${isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-slate-50 border-slate-200'}`} data-testid={`stat-config-${sport}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{sport}</span>
                  <Badge variant="outline" className="text-xs">{(data.categories || []).length} stats</Badge>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(data.categories || []).map(cat => (
                    <Badge key={cat} className={`text-xs ${isDark ? 'bg-slate-600 text-slate-200' : 'bg-slate-200 text-slate-700'}`}>
                      {(data.display_labels || {})[cat] || cat} ({cat})
                    </Badge>
                  ))}
                </div>
              </div>
            )) : (
              <div className={`text-center py-8 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                <BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Loading stat configuration...</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function TeamEditForm({ team, onSave, saving, isDark }) {
  const [cost, setCost] = useState(team.season_cost || 0);
  const [duration, setDuration] = useState(team.game_duration_minutes || 90);
  const [maxAge, setMaxAge] = useState(team.eligibility?.max_age || 15);

  return (
    <div className={`mt-3 pt-3 border-t ${isDark ? 'border-slate-600' : 'border-slate-200'} grid grid-cols-2 md:grid-cols-4 gap-3`}>
      <div>
        <Label className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Season Cost ($)</Label>
        <Input type="number" min="0" value={cost} onChange={e => setCost(parseFloat(e.target.value) || 0)} className={`mt-1 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}`} data-testid="edit-cost" />
      </div>
      <div>
        <Label className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Duration (min)</Label>
        <Input type="number" min="30" max="300" value={duration} onChange={e => setDuration(parseInt(e.target.value) || 90)} className={`mt-1 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}`} data-testid="edit-duration" />
      </div>
      <div>
        <Label className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Max Age</Label>
        <Input type="number" min="5" max="25" value={maxAge} onChange={e => setMaxAge(parseInt(e.target.value) || 15)} className={`mt-1 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}`} data-testid="edit-max-age" />
      </div>
      <div className="flex items-end">
        <Button className="w-full bg-amber-600 hover:bg-amber-700" onClick={() => onSave(team.id, { season_cost: cost, game_duration_minutes: duration, eligibility: { max_age: maxAge } })} disabled={saving} data-testid="save-team-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-3 w-3 mr-1" />Save</>}
        </Button>
      </div>
    </div>
  );
}

function QuestionDialog({ open, onClose, onSave, isDark, questions }) {
  const [label, setLabel] = useState('');
  const [type, setType] = useState('text');
  const [section, setSection] = useState('player');
  const [required, setRequired] = useState(false);
  const [options, setOptions] = useState('');
  const [condTrigger, setCondTrigger] = useState('');
  const [condValue, setCondValue] = useState('');

  const handleSave = () => {
    const q = { label, type, section, required, options: options ? options.split(',').map(o => o.trim()) : [], order: questions.length + 1 };
    if (condTrigger) q.conditional = { trigger_question_id: condTrigger, trigger_value: condValue };
    onSave(q);
    setLabel(''); setType('text'); setRequired(false); setOptions(''); setCondTrigger(''); setCondValue('');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
        <DialogHeader><DialogTitle className={isDark ? 'text-white' : 'text-slate-900'}>Add Registration Question</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className={isDark ? 'text-slate-300' : ''}>Question Label *</Label><Input value={label} onChange={e => setLabel(e.target.value)} className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="q-label" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className={isDark ? 'text-slate-300' : ''}>Type</Label>
              <select value={type} onChange={e => setType(e.target.value)} className={`w-full h-10 rounded-md px-3 border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}`} data-testid="q-type">
                <option value="text">Text</option><option value="textarea">Text Area</option><option value="select">Select</option><option value="checkbox">Checkbox</option><option value="date">Date</option>
              </select></div>
            <div><Label className={isDark ? 'text-slate-300' : ''}>Section</Label>
              <select value={section} onChange={e => setSection(e.target.value)} className={`w-full h-10 rounded-md px-3 border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}`} data-testid="q-section">
                <option value="player">Player</option><option value="family">Family</option><option value="medical">Medical</option>
              </select></div>
          </div>
          {type === 'select' && <div><Label className={isDark ? 'text-slate-300' : ''}>Options (comma-separated)</Label><Input value={options} onChange={e => setOptions(e.target.value)} placeholder="Yes, No" className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="q-options" /></div>}
          <div className="flex items-center gap-2"><input type="checkbox" checked={required} onChange={e => setRequired(e.target.checked)} id="q-req" /><Label htmlFor="q-req" className={isDark ? 'text-slate-300' : ''}>Required</Label></div>
          <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700/30' : 'bg-slate-50'}`}>
            <p className={`text-xs font-medium mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Conditional Follow-up (optional)</p>
            <div className="grid grid-cols-2 gap-2">
              <select value={condTrigger} onChange={e => setCondTrigger(e.target.value)} className={`h-8 rounded-md px-2 border text-xs ${isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}`} data-testid="q-cond-trigger">
                <option value="">None</option>
                {questions.map(q => <option key={q.id} value={q.id}>{q.label}</option>)}
              </select>
              <Input value={condValue} onChange={e => setCondValue(e.target.value)} placeholder="Trigger value" className={`h-8 text-xs ${isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}`} data-testid="q-cond-value" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleSave} disabled={!label} data-testid="save-question-btn">Add Question</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DocDialog({ open, onClose, onSave, isDark }) {
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
        <DialogHeader><DialogTitle className={isDark ? 'text-white' : 'text-slate-900'}>Add Document Agreement</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className={isDark ? 'text-slate-300' : ''}>Document Title *</Label><Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g., Code of Conduct" className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="doc-title" /></div>
          <div><Label className={isDark ? 'text-slate-300' : ''}>Document URL *</Label><Input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://..." className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="doc-url" /></div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => { onSave({ title, url, required: true }); setTitle(''); setUrl(''); }} disabled={!title || !url} data-testid="save-doc-btn">Add Document</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ApprovedTeamDialog({ open, onClose, onSave, isDark }) {
  const [orgName, setOrgName] = useState('');
  const [teamName, setTeamName] = useState('');
  const [email, setEmail] = useState('');
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
        <DialogHeader><DialogTitle className={isDark ? 'text-white' : 'text-slate-900'}>Add Approved Team</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className={isDark ? 'text-slate-300' : ''}>Organization Name *</Label><Input value={orgName} onChange={e => setOrgName(e.target.value)} placeholder="e.g., Chatham Thunder" className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="approved-org" /></div>
          <div><Label className={isDark ? 'text-slate-300' : ''}>Team Name</Label><Input value={teamName} onChange={e => setTeamName(e.target.value)} placeholder="e.g., Varsity Boys" className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="approved-team" /></div>
          <div><Label className={isDark ? 'text-slate-300' : ''}>Contact Email</Label><Input value={email} onChange={e => setEmail(e.target.value)} placeholder="scheduler@team.com" className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="approved-email" /></div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={() => { onSave({ org_name: orgName, team_name: teamName, contact_email: email }); setOrgName(''); setTeamName(''); setEmail(''); }} disabled={!orgName} data-testid="save-approved-btn">Add Team</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SportDateEditor({ sport, onSave, isDark }) {
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const [startMonth, setStartMonth] = useState(sport.season_start_month || 1);
  const [endMonth, setEndMonth] = useState(sport.season_end_month || 12);
  const [changed, setChanged] = useState(false);
  return (
    <div className={`flex items-center gap-3 py-2 px-3 mb-2 rounded-lg ${isDark ? 'bg-slate-700/30' : 'bg-slate-50'}`}>
      <span className={`text-sm font-medium flex-1 ${isDark ? 'text-white' : 'text-slate-800'}`}>{sport.name}</span>
      <div className="flex items-center gap-2">
        <select value={startMonth} onChange={e => { setStartMonth(parseInt(e.target.value)); setChanged(true); }}
          className={`h-8 rounded px-2 text-xs border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}`}>
          {MONTHS.map((m, idx) => <option key={idx} value={idx + 1}>{m}</option>)}
        </select>
        <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>to</span>
        <select value={endMonth} onChange={e => { setEndMonth(parseInt(e.target.value)); setChanged(true); }}
          className={`h-8 rounded px-2 text-xs border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}`}>
          {MONTHS.map((m, idx) => <option key={idx} value={idx + 1}>{m}</option>)}
        </select>
        {changed && (
          <Button size="sm" className="h-7 bg-blue-600 hover:bg-blue-700 text-xs px-2" 
            onClick={() => { onSave(sport.id, { season_start_month: startMonth, season_end_month: endMonth }); setChanged(false); }}>
            <Save className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
}

function AddDivisionForm({ leagueId, isDark, onSave, onClose }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [region, setRegion] = useState('');
  const [maxTeams, setMaxTeams] = useState(0);

  return (
    <div className="space-y-4">
      <div>
        <Label className={isDark ? 'text-slate-300' : ''}>Division Name *</Label>
        <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Eastern Conference"
          className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="division-name-input" />
      </div>
      <div>
        <Label className={isDark ? 'text-slate-300' : ''}>Description</Label>
        <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional description"
          className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="division-desc-input" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className={isDark ? 'text-slate-300' : ''}>Region</Label>
          <Input value={region} onChange={e => setRegion(e.target.value)} placeholder="e.g., East, West"
            className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="division-region-input" />
        </div>
        <div>
          <Label className={isDark ? 'text-slate-300' : ''}>Max Teams (0=unlimited)</Label>
          <Input type="number" min="0" value={maxTeams} onChange={e => setMaxTeams(parseInt(e.target.value) || 0)}
            className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="division-max-input" />
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button className="bg-purple-600 hover:bg-purple-700" disabled={!name}
          onClick={() => onSave({ name, description, region, max_teams: maxTeams })} data-testid="save-division-btn">
          Create Division
        </Button>
      </div>
    </div>
  );
}


export function PublishTriggersSection({ orgId, isDark }) {
  const [triggers, setTriggers] = useState({
    notify_coaches: true,
    notify_ad: true,
    referee_agency_email: '',
    custom_emails: [],
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [newEmail, setNewEmail] = useState('');

  const text = isDark ? 'text-slate-300' : 'text-slate-600';

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/game-schedule/${orgId}/publish-triggers`, { withCredentials: true });
        if (data.publish_triggers) setTriggers(data.publish_triggers);
      } catch {}
    };
    if (orgId) fetch();
  }, [orgId]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/game-schedule/${orgId}/publish-triggers`, triggers, { withCredentials: true });
      setMsg('Publish triggers saved');
      setTimeout(() => setMsg(''), 3000);
    } catch {}
    setSaving(false);
  };

  const addCustomEmail = () => {
    if (newEmail && newEmail.includes('@')) {
      setTriggers(t => ({ ...t, custom_emails: [...(t.custom_emails || []), newEmail] }));
      setNewEmail('');
    }
  };

  const removeCustomEmail = (idx) => {
    setTriggers(t => ({ ...t, custom_emails: (t.custom_emails || []).filter((_, i) => i !== idx) }));
  };

  return (
    <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
      <CardHeader className="pb-2">
        <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
          <Send className="h-5 w-5 text-blue-500" />Publish Notification Triggers
        </CardTitle>
        <p className={`text-sm mt-1 ${text}`}>Configure who gets notified when a schedule is published</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {msg && <div className="bg-green-500/10 border border-green-500/50 rounded-lg p-2 text-green-400 text-sm flex items-center gap-2"><CheckCircle2 className="h-4 w-4" />{msg}</div>}

        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer" data-testid="trigger-coaches">
            <input type="checkbox" checked={triggers.notify_coaches}
              onChange={e => setTriggers(t => ({ ...t, notify_coaches: e.target.checked }))}
              className="h-4 w-4 rounded" />
            <div>
              <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Notify Coaches</span>
              <p className={`text-xs ${text}`}>All head coaches and assistant coaches receive an email</p>
            </div>
          </label>

          <label className="flex items-center gap-3 cursor-pointer" data-testid="trigger-ad">
            <input type="checkbox" checked={triggers.notify_ad}
              onChange={e => setTriggers(t => ({ ...t, notify_ad: e.target.checked }))}
              className="h-4 w-4 rounded" />
            <div>
              <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Notify Athletic Director</span>
              <p className={`text-xs ${text}`}>AD receives a confirmation email when schedule is published</p>
            </div>
          </label>
        </div>

        <div>
          <Label className={isDark ? 'text-slate-300' : ''}>Referee Agency Email</Label>
          <Input value={triggers.referee_agency_email}
            onChange={e => setTriggers(t => ({ ...t, referee_agency_email: e.target.value }))}
            placeholder="referees@agency.com"
            className={`mt-1 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}`}
            data-testid="referee-email-input" />
          <p className={`text-xs mt-1 ${text}`}>Optional: referee agency receives schedule details on publish</p>
        </div>

        <div>
          <Label className={isDark ? 'text-slate-300' : ''}>Additional Notification Emails</Label>
          <div className="flex gap-2 mt-1">
            <Input value={newEmail} onChange={e => setNewEmail(e.target.value)}
              placeholder="extra@email.com" onKeyDown={e => e.key === 'Enter' && addCustomEmail()}
              className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}
              data-testid="custom-email-input" />
            <Button size="sm" variant="outline" onClick={addCustomEmail} data-testid="add-custom-email-btn">
              <Plus className="h-3 w-3" />
            </Button>
          </div>
          {(triggers.custom_emails || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {triggers.custom_emails.map((email, idx) => (
                <Badge key={idx} variant="outline" className="flex items-center gap-1">
                  <Mail className="h-3 w-3" />{email}
                  <button onClick={() => removeCustomEmail(idx)} className="ml-1 hover:text-red-500">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-triggers-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" />Save Triggers</>}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Assignment 1 (migration_log_0168): the games-publish-flag mode off-switch.
// OFF (default) = today's Games/Scheduler/sync behaviour byte-for-byte. ON routes
// Games-tab writes SOT-first (historical_games) + projects ol_games through the
// single mirror writer. Shipped OFF; turning it ON is a later assignment.
export function GamesPublishFlagSection({ orgId, isDark }) {
  const [mode, setMode] = useState('off');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const text = isDark ? 'text-slate-300' : 'text-slate-600';

  useEffect(() => {
    const f = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/ol-canonical/publish-flag-mode/${orgId}`, { withCredentials: true });
        setMode(data.games_publish_flag_mode || 'off');
      } catch {}
    };
    if (orgId) f();
  }, [orgId]);

  const toggle = async () => {
    const next = mode === 'on' ? 'off' : 'on';
    setBusy(true);
    try {
      await axios.put(`${API_URL}/api/ol-canonical/publish-flag-mode/${orgId}`, { mode: next }, { withCredentials: true });
      setMode(next);
      setMsg(`Games publish-flag mode: ${next.toUpperCase()}`);
      setTimeout(() => setMsg(''), 3000);
    } catch {}
    setBusy(false);
  };

  return (
    <Card className={`mt-4 ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`} data-testid="games-publish-flag-card">
      <CardHeader className="pb-2">
        <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
          <ServerCog className="h-5 w-5 text-amber-500" />Games Publish-Flag Mode
        </CardTitle>
        <p className={`text-sm mt-1 ${text}`}>Advanced. OFF (default) keeps today's Games / Scheduler / sync behaviour exactly. ON makes the Games tab write the Game record (SOT) first and project the WordPress copy through one writer. Leave OFF unless instructed.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {msg && <div className="bg-green-500/10 border border-green-500/50 rounded-lg p-2 text-green-400 text-sm flex items-center gap-2"><CheckCircle2 className="h-4 w-4" />{msg}</div>}
        <label className="flex items-center gap-3 cursor-pointer" data-testid="games-publish-flag-toggle">
          <input type="checkbox" checked={mode === 'on'} disabled={busy} onChange={toggle} className="h-4 w-4 rounded" />
          <div>
            <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Enable SOT-first Games publishing</span>
            <p className={`text-xs ${text}`}>Current mode: <strong data-testid="games-publish-flag-mode">{mode.toUpperCase()}</strong></p>
          </div>
        </label>
      </CardContent>
    </Card>
  );
}

// WP Mgmt Defaults (migration_log_0174): which sport the Games tab opens on.
// Pure UI preference stored on ol_site_config.wp_mgmt_default_sport (a sport
// slug, the same value the Games sport dropdown emits: s.slug||s.id; '' = All).
export function WpMgmtDefaultsSection({ orgId, isDark }) {
  const [sports, setSports] = useState([]);
  const [sport, setSport] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const text = isDark ? 'text-slate-300' : 'text-slate-600';

  useEffect(() => {
    if (!orgId) return;
    (async () => {
      try {
        const [sp, df] = await Promise.all([
          axios.get(`${API_URL}/api/organizations/${orgId}/sports`, { withCredentials: true }),
          axios.get(`${API_URL}/api/ol-canonical/wp-mgmt-defaults/${orgId}`, { withCredentials: true }),
        ]);
        setSports(Array.isArray(sp.data) ? sp.data : []);
        setSport(df.data?.wp_mgmt_default_sport || '');
      } catch {}
    })();
  }, [orgId]);

  const save = async (val) => {
    setSport(val);
    setBusy(true);
    try {
      await axios.put(`${API_URL}/api/ol-canonical/wp-mgmt-defaults/${orgId}`, { sport: val }, { withCredentials: true });
      setMsg('Saved'); setTimeout(() => setMsg(''), 3000);
    } catch {}
    setBusy(false);
  };

  return (
    <Card className={`mt-4 ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`} data-testid="wp-mgmt-defaults-card">
      <CardHeader className="pb-2">
        <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
          <ServerCog className="h-5 w-5 text-amber-500" />WP Mgmt Defaults
        </CardTitle>
        <p className={`text-sm mt-1 ${text}`}>Which sport the WP Mgmt &rsaquo; Games tab opens on. The season still defaults to the current season for that sport.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {msg && <div className="bg-green-500/10 border border-green-500/50 rounded-lg p-2 text-green-400 text-sm flex items-center gap-2"><CheckCircle2 className="h-4 w-4" />{msg}</div>}
        <label className="block">
          <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Default sport for Games</span>
          <select
            value={sport}
            disabled={busy}
            onChange={e => save(e.target.value)}
            data-testid="wp-mgmt-default-sport-select"
            className={`mt-1 h-9 text-sm rounded-md px-2 w-full ${isDark ? 'bg-slate-900 border border-slate-600 text-slate-100' : 'bg-white border border-slate-300 text-slate-900'}`}
          >
            <option value="">All sports</option>
            {sports.map(s => (
              <option key={s.id || s.slug} value={s.slug || s.id}>{s.name || s.title || s.slug}</option>
            ))}
          </select>
        </label>
      </CardContent>
    </Card>
  );
}

